import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Typography,
  TextField,
  IconButton,
  Button,
  TablePagination,
  Box,
  Tooltip,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useCompetencia } from '../../context/CompetenciaContext';
import { Edit, Save, Cancel, Delete } from '@mui/icons-material';
import optimizeAndConvertToWebP from '../../utils/optimizeAndConvertToWebP';
import AddTeamModal from './AddTeamModal';
import { v4 as uuidv4 } from 'uuid';
import { generateTeamCodeFromName } from '../../utils/teamUtils';

const EditableTeamRow = ({ team, index, onUpdate, onDelete, existingTeamCodes }) => {
  const [editing, setEditing] = useState(false);
  const [editedTeam, setEditedTeam] = useState({ ...team });

  const handleNameChange = (e) => {
    const newName = e.target.value;
    const newTeamCode = generateTeamCodeFromName(newName, existingTeamCodes);
    setEditedTeam((prev) => ({
      ...prev,
      name: newName,
      teamCode: newTeamCode,
    }));
  };

  const handleTeamCodeChange = (e) => {
    setEditedTeam((prev) => ({ ...prev, teamCode: e.target.value }));
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const optimized = await optimizeAndConvertToWebP(file);
      if (optimized) {
        setEditedTeam((prev) => ({ ...prev, logoEquipo: optimized }));
      }
    }
  };

  const handleSave = () => {
    onUpdate(editedTeam, index, team.name);
    setEditing(false);
  };

  const handleCancel = () => {
    setEditedTeam({ ...team });
    setEditing(false);
  };

  const handleDeleteClick = () => {
    onDelete(team);
  };

  return (
    <TableRow>
      <TableCell>
        {editing ? (
          <>
            <input
              accept="image/*"
              type="file"
              onChange={handleLogoChange}
              style={{ display: 'none' }}
              id={`upload-logo-${index}`}
            />
            <label htmlFor={`upload-logo-${index}`}>
              <Tooltip title="Cambiar Logo">
                <IconButton component="span" size="small">
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            </label>
            <Avatar src={editedTeam.logoEquipo} sx={{ width: 40, height: 40 }} />
          </>
        ) : (
          <Avatar src={team.logoEquipo} sx={{ width: 40, height: 40 }}>
            {!team.logoEquipo && team.name ? team.name.charAt(0).toUpperCase() : ''}
          </Avatar>
        )}
      </TableCell>
      <TableCell>
        {editing ? (
          <TextField variant="standard" value={editedTeam.name} onChange={handleNameChange} />
        ) : (
          <Typography variant="body2">{team.name}</Typography>
        )}
      </TableCell>
      <TableCell>
        {editing ? (
          <TextField variant="standard" value={editedTeam.teamCode || ''} onChange={handleTeamCodeChange} />
        ) : (
          <Typography variant="body2">
            {team.teamCode ? team.teamCode : generateTeamCodeFromName(team.name, existingTeamCodes)}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {editing ? (
          <Box display="flex" gap={1}>
            <Tooltip title="Guardar">
              <IconButton onClick={handleSave} size="small">
                <Save fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cancelar">
              <IconButton onClick={handleCancel} size="small">
                <Cancel fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box display="flex" gap={1}>
            <Tooltip title="Editar">
              <IconButton onClick={() => setEditing(true)} size="small">
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar Equipo">
              <IconButton onClick={handleDeleteClick} size="small" color="error">
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};

EditableTeamRow.propTypes = {
  team: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  existingTeamCodes: PropTypes.array.isRequired,
};

const TeamsTable = () => {
  const { slug } = useParams();
  const { competencias, updateCompetencia } = useCompetencia();
  const competition = competencias.find((comp) => comp.slug === slug);

  const [teamsState, setTeamsState] = useState(() => {
    let initialTeams = [];
    if (competition?.equipos && competition.equipos.length > 0) {
      initialTeams = competition.equipos;
    } else if (competition?.inscripciones && competition.inscripciones.length > 0) {
      const uniqueTeamNames = Array.from(
        new Set(
          competition.inscripciones
            .map((insc) =>
              typeof insc.equipo === 'object' ? insc.equipo.name : insc.equipo
            )
            .filter(Boolean)
        )
      );
      initialTeams = uniqueTeamNames.map((name) => ({
        id: uuidv4(),
        name,
        logoEquipo: '',
        teamCode: generateTeamCodeFromName(name, []),
      }));
    }
    return initialTeams;
  });

  useEffect(() => {
    if (competition) {
      let newTeams = [];
      if (competition.equipos && competition.equipos.length > 0) {
        newTeams = competition.equipos;
      } else if (competition.inscripciones && competition.inscripciones.length > 0) {
        const uniqueTeamNames = Array.from(
          new Set(
            competition.inscripciones
              .map((insc) =>
                typeof insc.equipo === 'object' ? insc.equipo.name : insc.equipo
              )
              .filter(Boolean)
          )
        );
        newTeams = uniqueTeamNames.map((name) => ({
          id: uuidv4(),
          name,
          logoEquipo: '',
          teamCode: generateTeamCodeFromName(name, []),
        }));
      }
      setTeamsState(newTeams);
    }
  }, [competition?.equipos, competition?.inscripciones, competition]);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const handleUpdateTeam = (updatedTeam, index, oldName) => {
    const newTeams = [...teamsState];
    newTeams[index] = updatedTeam;
    setTeamsState(newTeams);
    // Actualizar inscripciones que referencian el equipo (por id o por nombre)
    const updatedInscripciones = competition.inscripciones.map((insc) => {
      if (typeof insc.equipo === 'object' && insc.equipo.id === updatedTeam.id) {
        return { ...insc, equipo: updatedTeam };
      }
      if (typeof insc.equipo === 'string' && insc.equipo === oldName) {
        return { ...insc, equipo: updatedTeam };
      }
      return insc;
    });
    updateCompetencia({
      ...competition,
      equipos: newTeams,
      inscripciones: updatedInscripciones,
    });
  };

  const handleAddTeam = (newTeam) => {
    const newTeams = [...teamsState, newTeam];
    setTeamsState(newTeams);
    updateCompetencia({ ...competition, equipos: newTeams });
  };

  const handleDeleteTeam = (teamToDelete) => {
    const newTeams = teamsState.filter((t) => t.id !== teamToDelete.id);
    setTeamsState(newTeams);
    const updatedInscripciones = competition.inscripciones.map((insc) => {
      if (typeof insc.equipo === 'object' && insc.equipo.id === teamToDelete.id) {
        return { ...insc, equipo: '' };
      } else if (typeof insc.equipo === 'string' && insc.equipo === teamToDelete.name) {
        return { ...insc, equipo: '' };
      }
      return insc;
    });
    updateCompetencia({
      ...competition,
      equipos: newTeams,
      inscripciones: updatedInscripciones,
    });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <>
      <Box
        sx={{
          maxWidth: 800,
          margin: 'auto',
          mt: 2,
          mb: 2,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Button variant="contained" color="primary" onClick={() => setOpenAddModal(true)}>
          Agregar Equipo
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ maxWidth: 800, margin: 'auto', mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  Logo
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  Equipo
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  Team Code
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle1" fontWeight="bold">
                  Acciones
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teamsState.length > 0 ? (
              teamsState.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((team, index) => (
                <EditableTeamRow
                  key={team.id}
                  team={team}
                  index={index}
                  onUpdate={handleUpdateTeam}
                  onDelete={handleDeleteTeam}
                  existingTeamCodes={teamsState.map((t) => t.teamCode)}
                />
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2">No hay equipos registrados.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={teamsState.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <AddTeamModal open={openAddModal} onClose={() => setOpenAddModal(false)} onAdd={handleAddTeam} />
    </>
  );
};

TeamsTable.propTypes = {};

export default TeamsTable;
